import { FC } from 'react';
import { Select as SelectComponent, MenuItem } from 'ui/Select';
import { FormControl } from 'ui/FormControl';
import { SxProps } from '@mui/material';
import { styles } from './styles';
import { Box } from 'ui/Box';
import { Badge } from 'components/Badge';
import { SvgIconProps } from 'types/styles';
import { CrossIcon } from 'components/icons/CrossIcon';
import { ArrowDownIcon } from 'components/icons/ArrowDownIcon';
import { Typography } from 'ui/Typography';

export type DropdownOption = {
  label: string;
  value: string;
  disabled?: boolean;
};

export interface DropdownProps {
  value?: string | string[];
  options: DropdownOption[];
  onChange?: (value: string | string[]) => void;
  sx?: SxProps;
  icon?: FC<SvgIconProps>;
  rotateIcon?: boolean;
  placeholder?: string;
  multiple?: boolean;
  disabled?: boolean;
  name?: string;
  error?: string;
}

export const Select: FC<DropdownProps> = ({
  options,
  value,
  onChange,
  sx,
  icon: IconComponent = ArrowDownIcon,
  rotateIcon = true,
  multiple = false,
  placeholder = 'Choose from the list',
  disabled = false,
  name = '',
  error,
}) => {
  const selectStyles = {
    ...styles.root,
    ...sx,
    ...(rotateIcon ? {} : styles.rotateIcon),
    ...(error ? styles.error : {}),
  };

  const renderSelectedValue = (selected: string | string[]) => {
    const selectedValues = Array.isArray(selected) ? selected : [selected];
    if (!selectedValues.length || selectedValues[0] === '') {
      return <Box sx={styles.placeholder}>{placeholder}</Box>;
    }
    if (!multiple) {
      const selectedItem = options.find(
        (option) => option.value === (selected as string)
      );
      return <Box>{selectedItem!.label}</Box>;
    }
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {selectedValues.map((value) => {
          const option = options.find((option) => option.value === value);
          return (
            <Badge
              key={value}
              name={option?.label || ''}
              className="px-2 py-1"
            />
          );
        })}
      </Box>
    );
  };

  return (
    <FormControl sx={styles.container} fullWidth>
      <SelectComponent
        name={name}
        disabled={disabled}
        multiple={multiple}
        value={value}
        onChange={(event) => onChange && onChange(event.target.value)}
        displayEmpty
        IconComponent={IconComponent}
        sx={selectStyles as SxProps}
        autoWidth
        renderValue={renderSelectedValue}
        MenuProps={{
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          transformOrigin: { vertical: 'top', horizontal: 'left' },
          PaperProps: { sx: styles.paper },
        }}
      >
        {options.map(({ value: optionValue, label, disabled }) => {
          const isSelected = Array.isArray(value)
            ? value.includes(optionValue)
            : value === optionValue;
          return (
            <MenuItem
              key={optionValue}
              value={optionValue}
              disabled={disabled}
              sx={styles.menuItem}
            >
              {label} {isSelected && <CrossIcon size="sm" />}
            </MenuItem>
          );
        })}
      </SelectComponent>
      {error && (
        <Typography variant="caption" sx={styles.errorText}>
          {error}
        </Typography>
      )}
    </FormControl>
  );
};
